import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Alert from "../../../Common/Alert";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import {
  ChartOfAccountStyles,
  formatDate,
  num,
} from "../../Accounts/Chart-Of-Account/ChartOfAccountShared";
import {
  AmountCell,
  FinancialStatementStyles,
  NodeNameCell,
  PresetChips,
  amount,
  endOfLastMonth,
  endOfLastYear,
  expandableKeys,
  isNodeRow,
  nodeKey,
  printStatement,
  pushNodeRows,
  rowAmounts,
  today,
} from "./FinancialStatementShared";

const TITLE = "Statement of Financial Position";

// Flattens the statement into the rows shown on screen and in print, so both
// always agree.
const buildRows = (data, expanded, hideZero) => {
  const rows = [];

  const pushGroup = (label, lines, total, compareTotal, key) => {
    rows.push({ type: "group", key: `g-${key}`, label });
    pushNodeRows(rows, lines, 1, expanded, hideZero);
    rows.push({
      type: "subtotal",
      key: `s-${key}`,
      label: `Total ${label}`,
      amount: total,
      compare_amount: compareTotal,
    });
  };

  rows.push({ type: "section", key: "sec-assets", label: "Assets" });
  data.assets.groups.forEach((group) =>
    pushGroup(
      group.name,
      group.children,
      group.amount,
      group.compare_amount,
      group.id,
    ),
  );
  rows.push({
    type: "total",
    key: "t-assets",
    label: "Total Assets",
    amount: data.assets.total,
    compare_amount: data.assets.compare_total,
  });

  rows.push({
    type: "section",
    key: "sec-eq-liab",
    label: "Equity and Liabilities",
  });
  pushGroup(
    "Equity",
    data.equity.lines,
    data.equity.total,
    data.equity.compare_total,
    "equity",
  );
  data.liabilities.groups.forEach((group) =>
    pushGroup(
      group.name,
      group.children,
      group.amount,
      group.compare_amount,
      group.id,
    ),
  );
  rows.push({
    type: "total",
    key: "t-liabilities",
    label: "Total Liabilities",
    amount: data.liabilities.total,
    compare_amount: data.liabilities.compare_total,
  });
  rows.push({
    type: "grand",
    key: "t-grand",
    label: "Total Equity and Liabilities",
    amount: data.total_equity_and_liabilities,
    compare_amount: data.compare_total_equity_and_liabilities,
  });

  return rows;
};

const StatementOfFinancialPosition = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [inputs, setInputs] = useState({
    as_of: searchParams.get("as_of") || today(),
    compare_to: searchParams.get("compare_to") || "",
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [hideZero, setHideZero] = useState(false);

  const fetchData = async (params = inputs) => {
    setLoading(true);
    setAlert(null);

    const query = { as_of: params.as_of };
    if (params.compare_to) {
      query.compare_to = params.compare_to;
    }
    setSearchParams(query, { replace: true });

    try {
      const res = await Http.get("/statement-of-financial-position", {
        params: query,
      });
      setData(res.data);
    } catch (error) {
      console.error("Fetch error:", error);
      setData(null);
      setAlert({
        message:
          error.response?.data?.message ||
          "Could not load the statement. Please try again.",
        type: "danger",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const applyPreset = (name, value) => {
    const next = { ...inputs, [name]: value };
    setInputs(next);
    fetchData(next);
  };

  const hasCompare = !!(data && data.compare_to);

  const rows = useMemo(
    () => (data ? buildRows(data, expanded, hideZero) : []),
    [data, expanded, hideZero],
  );

  const allKeys = useMemo(
    () =>
      data
        ? expandableKeys([
            ...data.assets.groups.flatMap((group) => group.children),
            ...data.equity.lines,
            ...data.liabilities.groups.flatMap((group) => group.children),
          ])
        : [],
    [data],
  );

  const allExpanded =
    allKeys.length > 0 && allKeys.every((key) => expanded[key]);

  const toggleAll = () =>
    setExpanded(
      allExpanded
        ? {}
        : allKeys.reduce((acc, key) => ({ ...acc, [key]: true }), {}),
    );

  const toggle = (node) => {
    const key = nodeKey(node);
    setExpanded((values) => ({ ...values, [key]: !values[key] }));
  };

  const difference = data ? num(data.difference) : 0;
  const balanced = Math.abs(difference) < 0.005;
  const compareBalanced =
    !hasCompare || Math.abs(num(data.compare_difference)) < 0.005;

  const columnCount = hasCompare ? 5 : 3;

  const renderRow = (row) => {
    if (row.type === "section" || row.type === "group") {
      return (
        <tr key={row.key} className={`fs-${row.type}`}>
          <td colSpan={columnCount + 1}>{row.label}</td>
        </tr>
      );
    }

    const { amount: value, compare } = rowAmounts(row);
    const isNode = isNodeRow(row);

    return (
      <tr key={row.key} className={`fs-${row.type}`}>
        {isNode ? (
          <NodeNameCell
            row={row}
            onToggle={toggle}
            note={
              row.node.is_profit &&
              "Income less expenses up to the statement date, not yet closed to equity"
            }
          />
        ) : (
          <td>{row.label}</td>
        )}
        <td className="fs-code">{isNode ? row.node.code : ""}</td>
        <AmountCell value={value} />
        {hasCompare && <AmountCell value={compare} />}
        {hasCompare && <AmountCell value={num(value) - num(compare)} />}
      </tr>
    );
  };

  const handlePrint = () => {
    const headHtml = `
      <th>Particulars</th>
      <th>Code</th>
      <th class="num">${formatDate(data.as_of)}</th>
      ${
        hasCompare
          ? `<th class="num">${formatDate(data.compare_to)}</th><th class="num">Change</th>`
          : ""
      }`;

    const bodyHtml = rows
      .map((row) => {
        if (row.type === "section" || row.type === "group") {
          return `<tr class="${row.type}"><td colspan="${columnCount + 1}">${row.label}</td></tr>`;
        }
        const { amount: value, compare } = rowAmounts(row);
        const isNode = isNodeRow(row);
        const pad = isNode
          ? 16 + row.depth * 16
          : row.type === "subtotal"
            ? 32
            : 8;
        return `
          <tr class="${row.type}">
            <td style="padding-left:${pad}px">${isNode ? row.node.name : row.label}</td>
            <td class="code">${isNode && row.node.code ? row.node.code : ""}</td>
            <td class="num">${amount(value)}</td>
            ${
              hasCompare
                ? `<td class="num">${amount(compare)}</td><td class="num">${amount(num(value) - num(compare))}</td>`
                : ""
            }
          </tr>`;
      })
      .join("");

    printStatement({
      title: TITLE,
      subtitle: `As at ${formatDate(data.as_of)}${
        hasCompare ? ` (compared with ${formatDate(data.compare_to)})` : ""
      } &middot; Amounts in Taka`,
      headHtml,
      bodyHtml,
      note: balanced
        ? "Total assets equal total equity and liabilities."
        : `Out of balance by ${amount(difference)}.`,
    });
  };

  const stat = (label, icon, value, compare) => (
    <div className="col-md-3 mg-b-10">
      <div className="coa-stat">
        <i className={`fa ${icon} coa-stat-icon`}></i>
        <span className="coa-stat-label">{label}</span>
        <span className="coa-stat-value">{amount(value)}</span>
        <span className="coa-stat-sub">
          {hasCompare
            ? `${amount(compare)} at ${formatDate(data.compare_to)}`
            : `As at ${formatDate(data.as_of)}`}
        </span>
      </div>
    </div>
  );

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <ChartOfAccountStyles />
        <FinancialStatementStyles />
        <div className="am-pagetitle">
          <div>
            <h5 className="am-title mb-1">{TITLE}</h5>
            <p className="text-muted mb-0 tx-12">
              Balance sheet as at {formatDate(data?.as_of || inputs.as_of)}
              {hasCompare && ` compared with ${formatDate(data.compare_to)}`}
            </p>
          </div>
          <div className="d-flex gap-2 coa-no-print">
            <Link to="/reports" className="btn btn-outline-secondary">
              <i className="fa fa-arrow-left mg-r-6"></i>Reports
            </Link>
            <button
              className="btn btn-outline-secondary"
              onClick={() => fetchData()}
              title="Reload data"
              disabled={loading}
            >
              <i className="fa fa-refresh"></i>
            </button>
            <button
              className="btn btn-primary"
              onClick={handlePrint}
              disabled={loading || !data}
            >
              <i className="fa fa-print mg-r-6"></i>Print
            </button>
          </div>
        </div>

        <div className="am-pagebody">
          <Alert alert={alert} />

          <div className="coa-panel coa-no-print">
            <div className="coa-panel-head">
              <span>
                <i className="fa fa-calendar-o me-2"></i>Statement Date
              </span>
              <span className="coa-head-note">
                Balances include every posting up to the date
              </span>
            </div>
            <div className="coa-panel-body">
              <div className="row align-items-start">
                <div className="col-lg-4">
                  <label>As at</label>
                  <input
                    type="date"
                    className="form-control"
                    name="as_of"
                    value={inputs.as_of}
                    onChange={handleChange}
                  />
                  <PresetChips
                    presets={[
                      ["Today", today()],
                      ["Last month end", endOfLastMonth()],
                      ["Last year end", endOfLastYear()],
                    ]}
                    isActive={([, value]) => inputs.as_of === value}
                    onPick={([, value]) => applyPreset("as_of", value)}
                  />
                </div>
                <div className="col-lg-4 mg-t-10 mg-lg-t-0">
                  <label>Compare with (optional)</label>
                  <input
                    type="date"
                    className="form-control"
                    name="compare_to"
                    value={inputs.compare_to}
                    onChange={handleChange}
                  />
                  <PresetChips
                    presets={[
                      ["Last month end", endOfLastMonth()],
                      ["Last year end", endOfLastYear()],
                      ...(inputs.compare_to ? [["No comparison", ""]] : []),
                    ]}
                    isActive={([label, value]) =>
                      label !== "No comparison" && inputs.compare_to === value
                    }
                    onPick={([, value]) => applyPreset("compare_to", value)}
                  />
                </div>
                <div className="col-lg-4 mg-t-10 mg-lg-t-0">
                  <label>&nbsp;</label>
                  <button
                    className="btn btn-primary"
                    onClick={() => fetchData()}
                    disabled={loading || !inputs.as_of}
                  >
                    <i
                      className={`fa ${loading ? "fa-spinner fa-spin" : "fa-search"} mg-r-6`}
                    ></i>
                    {loading ? "Loading..." : "Apply"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {data && (
            <div className="row mg-b-20">
              {stat(
                "Total Assets",
                "fa-building-o",
                data.assets.total,
                data.assets.compare_total,
              )}
              {stat(
                "Total Liabilities",
                "fa-credit-card",
                data.liabilities.total,
                data.liabilities.compare_total,
              )}
              {stat(
                "Total Equity",
                "fa-pie-chart",
                data.equity.total,
                data.equity.compare_total,
              )}
              <div className="col-md-3 mg-b-10">
                <div
                  className={`coa-stat ${balanced && compareBalanced ? "is-ok" : "is-alert"}`}
                >
                  <i className="fa fa-balance-scale coa-stat-icon"></i>
                  <span className="coa-stat-label">Balance Check</span>
                  <span className="coa-stat-value">
                    {balanced && compareBalanced ? "Balanced" : "Out of balance"}
                  </span>
                  <span className="coa-stat-sub">
                    {balanced && compareBalanced
                      ? "Assets equal equity and liabilities"
                      : `Difference ${amount(
                          balanced ? data.compare_difference : difference,
                        )}${balanced ? ` at ${formatDate(data.compare_to)}` : ""}`}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="coa-panel">
            <div className="coa-panel-head">
              <span>
                <i className="fa fa-file-text-o me-2"></i>
                {TITLE}
                <span className="coa-head-note mg-l-10">Amounts in Taka</span>
              </span>
              {data && (
                <span className="d-flex align-items-center gap-3 coa-no-print">
                  <label className="fs-switch">
                    <input
                      type="checkbox"
                      checked={hideZero}
                      onChange={(e) => setHideZero(e.target.checked)}
                    />
                    Hide zero balances
                  </label>
                  {allKeys.length > 0 && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={toggleAll}
                    >
                      <i
                        className={`fa fa-${allExpanded ? "compress" : "expand"} mg-r-6`}
                      ></i>
                      {allExpanded ? "Collapse all" : "Expand all"}
                    </button>
                  )}
                </span>
              )}
            </div>

            {loading && !data ? (
              <div className="pd-y-10">
                {[...Array(10)].map((_, i) => (
                  <div key={i} className="fs-skeleton"></div>
                ))}
              </div>
            ) : !data ? (
              <div className="coa-empty">
                <i className="fa fa-exclamation-circle"></i>
                The statement could not be loaded.
              </div>
            ) : (
              <div className="table-responsive">
                <table className="fs-table">
                  <thead>
                    <tr>
                      <th>Particulars</th>
                      <th>Code</th>
                      <th className="coa-num">{formatDate(data.as_of)}</th>
                      {hasCompare && (
                        <th className="coa-num">
                          {formatDate(data.compare_to)}
                        </th>
                      )}
                      {hasCompare && <th className="coa-num">Change</th>}
                    </tr>
                  </thead>
                  <tbody style={{ opacity: loading ? 0.5 : 1 }}>
                    {rows.map(renderRow)}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {data && !balanced && (
            <div className="coa-notice is-alert">
              <i className="fa fa-exclamation-triangle"></i>
              <span>
                Total assets differ from total equity and liabilities by{" "}
                <strong>{amount(difference)}</strong>. This usually means
                postings are sitting on accounts outside the Assets,
                Liabilities, Equity, Revenue and Expenditure trees, or on an
                account without an account type.
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StatementOfFinancialPosition;
